/**
 * Work-email enforcement, shared by every surface that captures a B2B identity:
 * customer registration (`apps/api` auth), and the marketing lead magnets
 * ("try it live", "interested"). Centralized here so all of them reject the same
 * set of consumer mailboxes and never drift apart.
 *
 * The list is intentionally NOT exhaustive — it's the common free/consumer
 * providers. We block these rather than allow-listing corporate domains so a
 * genuine work address on any custom domain still gets through.
 */
export const FREE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.ca',
  'ymail.com',
  'rocketmail.com',
  'hotmail.com',
  'hotmail.co.uk',
  'outlook.com',
  'live.com',
  'msn.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'gmx.com',
  'gmx.net',
  'mail.com',
  'zoho.com',
  'yandex.com',
  'hey.com',
  'fastmail.com',
  'comcast.net',
  'verizon.net',
  'att.net',
  'sbcglobal.net',
  'bellsouth.net',
  'cox.net',
  'charter.net',
  'earthlink.net',
  'qq.com',
  '163.com',
  '126.com',
]);

/** The lowercased domain part of an email, or '' if it has no `@`. */
export function emailDomain(email: string): string {
  const at = email.lastIndexOf('@');
  return at === -1
    ? ''
    : email
        .slice(at + 1)
        .trim()
        .toLowerCase();
}

/**
 * True if `email` is from a known free/consumer provider (i.e. NOT a work
 * email). Trimmed + case-insensitive. Note: this does not validate that the
 * string is a well-formed email — pair it with a format check (Zod `.email()`).
 */
export function isFreeEmail(email: string): boolean {
  return FREE_EMAIL_DOMAINS.has(emailDomain(email));
}

/**
 * Disposable / throwaway mailbox providers (mailinator, temp-mail, …). A curated
 * subset of the most common ones — NOT exhaustive (the full universe is thousands
 * of churning domains). Blocking these keeps the lead list reachable and real.
 */
export const DISPOSABLE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  'mailinator.com',
  'guerrillamail.com',
  'guerrillamail.info',
  'guerrillamail.net',
  'sharklasers.com',
  'grr.la',
  'guerrillamailblock.com',
  '10minutemail.com',
  '10minutemail.net',
  'tempmail.com',
  'temp-mail.org',
  'tempmailo.com',
  'tempmail.plus',
  'tmail.gw',
  'tmpmail.org',
  'throwawaymail.com',
  'trashmail.com',
  'getnada.com',
  'nada.email',
  'dispostable.com',
  'mailnesia.com',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'mohmal.com',
  'fakeinbox.com',
  'fakemailgenerator.com',
  'maildrop.cc',
  'mailcatch.com',
  'spamgourmet.com',
  'mintemail.com',
  'mytemp.email',
  'tempinbox.com',
  'emailondeck.com',
  'burnermail.io',
  'moakt.com',
  'inboxkitten.com',
  'harakirimail.com',
  'anonbox.net',
  'getairmail.com',
  'spam4.me',
  'mailto.plus',
  'discard.email',
  'wegwerfmail.de',
  'einrot.com',
  'cuvox.de',
]);

/** True if `email` is from a known disposable/throwaway provider. */
export function isDisposableEmail(email: string): boolean {
  return DISPOSABLE_EMAIL_DOMAINS.has(emailDomain(email));
}

/**
 * True if `email` is NOT a work address — a free/consumer provider OR a
 * disposable mailbox. This is the gate every B2B identity-capture surface
 * (registration + lead magnets) should use.
 */
export function isBlockedEmail(email: string): boolean {
  const domain = emailDomain(email);
  return FREE_EMAIL_DOMAINS.has(domain) || DISPOSABLE_EMAIL_DOMAINS.has(domain);
}
